import React, { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Float, ContactShadows } from "@react-three/drei";

// R3F children built via React.createElement to avoid Emergent dev-time JSX
// instrumentation injecting non-DOM x-* props onto three.js primitives.
const h = React.createElement;

const SteelRod = ({ position = [0, 0, 0], rotation = [0, 0, 0], length = 5, radius = 0.18 }) => {
  const ref = useRef();
  useFrame((_s, delta) => {
    if (ref.current) {
      ref.current.rotation.x += delta * 0.15;
      ref.current.rotation.y += delta * 0.25;
    }
  });
  return h(
    "mesh",
    { ref, position, rotation, castShadow: true, receiveShadow: true },
    h("cylinderGeometry", { args: [radius, radius, length, 48] }),
    h("meshStandardMaterial", { color: "#9a9a9e", metalness: 1, roughness: 0.32 })
  );
};

const Ribbed = ({ position, rotation, length = 6 }) => {
  const group = useRef();
  useFrame((_s, d) => {
    if (group.current) {
      group.current.rotation.y += d * 0.3;
      group.current.rotation.z += d * 0.05;
    }
  });
  const rings = [];
  const spacing = 0.25;
  const count = Math.floor(length / spacing);
  for (let i = 0; i < count; i++) {
    rings.push(
      h(
        "mesh",
        {
          key: i,
          position: [0, -length / 2 + i * spacing, 0],
          rotation: [Math.PI / 2, 0, 0],
        },
        h("torusGeometry", { args: [0.23, 0.03, 8, 24] }),
        h("meshStandardMaterial", { color: "#c4c4c8", metalness: 1, roughness: 0.22 })
      )
    );
  }
  return h(
    "group",
    { ref: group, position, rotation },
    h(
      "mesh",
      null,
      h("cylinderGeometry", { args: [0.2, 0.2, length, 48] }),
      h("meshStandardMaterial", { color: "#a8a8ac", metalness: 1, roughness: 0.3 })
    ),
    ...rings
  );
};

const Scene = () => {
  return h(
    React.Fragment,
    null,
    h("ambientLight", { intensity: 0.65 }),
    h("directionalLight", { position: [5, 8, 4], intensity: 1.3, color: "#fff5e8" }),
    h("directionalLight", { position: [-6, -2, -4], intensity: 0.5, color: "#e85a1c" }),
    h("spotLight", { position: [0, 6, 3], angle: 0.4, penumbra: 1, intensity: 1.0 }),
    h(
      Float,
      { speed: 1.2, rotationIntensity: 0.4, floatIntensity: 0.6 },
      h(Ribbed, { position: [0, 0, 0], rotation: [0.3, 0.1, -0.35], length: 6 })
    ),
    h(
      Float,
      { speed: 0.9, rotationIntensity: 0.3, floatIntensity: 0.8 },
      h(SteelRod, { position: [-2.2, -0.6, -1.5], rotation: [-0.2, 0.4, 1.1], length: 4.5, radius: 0.14 })
    ),
    h(
      Float,
      { speed: 1.4, rotationIntensity: 0.5, floatIntensity: 0.7 },
      h(SteelRod, { position: [2.4, 0.8, -1.2], rotation: [0.6, -0.3, 0.9], length: 4, radius: 0.12 })
    ),
    h(ContactShadows, { position: [0, -2.2, 0], opacity: 0.25, scale: 10, blur: 3, far: 4 }),
    h(Environment, { preset: "studio" })
  );
};

export const Hero3D = () => {
  return (
    <div data-testid="hero-3d-canvas" className="hero-canvas">
      <Canvas
        camera={{ position: [0, 0, 7], fov: 45 }}
        dpr={[1, 2]}
        shadows
        gl={{ alpha: true, antialias: true }}
        style={{ background: "transparent" }}
      >
        <Suspense fallback={null}>{h(Scene)}</Suspense>
      </Canvas>
    </div>
  );
};

export default Hero3D;
